/**
 * TEMP-H-001: QCPanel Component
 * 
 * Displays QC persona details, rubric hash, weighted scores, and sub-scores.
 * Color-coded for pass/borderline/fail status with links to full reports.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  Hash, 
  Target, 
  FileText, 
  ExternalLink, 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  Clock
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { StageType } from '@/types/execution';
import { useQcSummary } from '@/hooks/useQcSummary';
import { cn } from '@/utils/cn';

interface QCPanelProps {
  executionId: string;
  stage: StageType;
  className?: string;
}

interface ScoreBarProps {
  label: string;
  value: number;
  threshold?: number;
  className?: string;
}

const ScoreBar: React.FC<ScoreBarProps> = ({ label, value, threshold, className }) => {
  const percentage = Math.round(value * 100);
  const isAboveThreshold = threshold ? value >= threshold : true;
  
  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex justify-between items-center text-xs">
        <span className="text-slate-300">{label}</span>
        <span className={cn(
          'font-medium',
          isAboveThreshold ? 'text-emerald-400' : 'text-amber-400'
        )}>
          {percentage}%
        </span>
      </div>
      <div className="w-full bg-slate-700/50 rounded-full h-2">
        <div
          className={cn(
            'h-2 rounded-full transition-all duration-300',
            isAboveThreshold 
              ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' 
              : 'bg-gradient-to-r from-amber-500 to-amber-400'
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {threshold && (
        <div className="text-xs text-slate-500">
          Threshold: {Math.round(threshold * 100)}%
        </div>
      )}
    </div>
  );
};

const PersonaBadge: React.FC<{ personaSlug: string; className?: string }> = ({ 
  personaSlug, 
  className 
}) => {
  // Map persona slugs to display names (based on existing stageAPI patterns)
  const getPersonaInfo = (slug: string) => {
    const personaMap: Record<string, { name: string; role: string }> = {
      'eval_script_narrative_quality': { name: 'The Editor', role: 'Script Quality' },
      'eval_vision_thematic_alignment': { name: 'The Architect', role: 'Vision Quality' },
      'eval_storyboard_visual_flow': { name: 'The Director', role: 'Storyboard' },
      'eval_shots_technical_feasibility': { name: 'The Operator', role: 'Shots Quality' },
      'eval_continuity_consistency': { name: 'The Inspector', role: 'Continuity' },
      'eval_elements_visual_quality': { name: 'The Artist', role: 'Elements Quality' },
      'eval_frames_style_adherence': { name: 'The Critic', role: 'Frames Quality' },
      'eval_video_production_quality': { name: 'The Producer', role: 'Video Quality' },
      'educator': { name: 'The Educator', role: 'Educational Content' },
    };
    
    return personaMap[slug] || { name: slug, role: 'Quality Evaluator' };
  };

  const persona = getPersonaInfo(personaSlug);
  
  return (
    <div className={cn(
      'flex items-center gap-2 bg-slate-800/50 border border-slate-600/30 rounded-lg px-3 py-2',
      className
    )}>
      <User className="w-4 h-4 text-blue-400" />
      <div>
        <div className="text-sm font-medium text-slate-200">{persona.name}</div>
        <div className="text-xs text-slate-400">{persona.role}</div>
      </div>
    </div>
  );
};

const DecisionBadge: React.FC<{ decision: string; weightedScore?: number }> = ({
  decision,
  weightedScore
}) => {
  const getDecisionStyle = (decision: string) => {
    switch (decision) {
      case 'pass':
        return {
          icon: CheckCircle,
          color: 'text-emerald-400',
          bg: 'bg-emerald-500/20',
          border: 'border-emerald-500/30'
        };
      case 'borderline':
        return {
          icon: AlertTriangle,
          color: 'text-amber-400',
          bg: 'bg-amber-500/20',
          border: 'border-amber-500/30'
        };
      case 'fail':
        return {
          icon: XCircle,
          color: 'text-red-400',
          bg: 'bg-red-500/20',
          border: 'border-red-500/30'
        };
      default:
        return {
          icon: Clock,
          color: 'text-slate-400',
          bg: 'bg-slate-500/20',
          border: 'border-slate-500/30'
        };
    }
  };

  const style = getDecisionStyle(decision);
  const Icon = style.icon;
  const percentage = weightedScore !== undefined ? Math.round(weightedScore * 100) : null;

  return (
    <div className={cn(
      'flex items-center gap-2 px-3 py-2 rounded-lg border',
      style.bg,
      style.border
    )}>
      <Icon className={cn('w-4 h-4', style.color)} />
      <div>
        <div className={cn('text-sm font-medium capitalize', style.color)}>
          {decision}
        </div>
        {percentage !== null && (
          <div className="text-xs text-slate-400">
            {percentage}% overall
          </div>
        )}
      </div>
    </div>
  );
};

const QCPanel: React.FC<QCPanelProps> = ({ executionId, stage, className }) => {
  const { data: qcSummary, isLoading, error } = useQcSummary(executionId, stage);

  if (isLoading) {
    return (
      <div className={cn('bg-slate-800/30 border border-slate-700/30 rounded-lg p-4', className)}>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-700/50 rounded w-32"></div>
          <div className="h-6 bg-slate-700/50 rounded w-24"></div>
          <div className="space-y-2">
            <div className="h-3 bg-slate-700/30 rounded"></div>
            <div className="h-2 bg-slate-700/30 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('bg-slate-800/30 border border-slate-700/30 rounded-lg p-4', className)}>
        <div className="text-center text-slate-400">
          <div className="text-sm">QC data unavailable</div>
          <div className="text-xs mt-1">{error.message}</div>
        </div>
      </div>
    );
  }

  if (!qcSummary) {
    return (
      <div className={cn('bg-slate-800/30 border border-slate-700/30 rounded-lg p-4', className)}>
        <div className="text-center text-slate-400">
          <Clock className="w-6 h-6 mx-auto mb-2 opacity-50" />
          <div className="text-sm">Awaiting QC evaluation</div>
          <div className="text-xs mt-1">Quality control analysis will appear here</div>
        </div>
      </div>
    );
  }

  const formattedTime = qcSummary.updatedAt
    ? formatDistanceToNow(new Date(qcSummary.updatedAt), { addSuffix: true })
    : 'Unknown';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 backdrop-blur-sm space-y-4',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-slate-200">Quality Control</h3>
        {qcSummary.updatedAt && (
          <div className="text-xs text-slate-400">
            Updated {formattedTime}
          </div>
        )}
      </div>

      {/* Persona & Rubric Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {qcSummary.personaSlug && (
          <PersonaBadge personaSlug={qcSummary.personaSlug} />
        )}

        {qcSummary.rubricHash && (
          <div className="flex items-center gap-2 bg-slate-800/50 border border-slate-600/30 rounded-lg px-3 py-2">
            <Hash className="w-4 h-4 text-purple-400" />
            <div>
              <div className="text-sm font-medium text-slate-200">Rubric</div>
              <div className="text-xs text-slate-400 font-mono">
                {qcSummary.rubricHash.slice(0, 8)}...
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Decision & Overall Score */}
      <div className="flex items-center gap-3">
        <DecisionBadge
          decision={qcSummary.decision}
          weightedScore={qcSummary.weightedScore}
        />

        {/* Overall Score Bar */}
        {qcSummary.weightedScore !== undefined && (
          <div className="flex-1">
            <ScoreBar
              label="Overall Score"
              value={qcSummary.weightedScore}
              className="mb-0"
            />
          </div>
        )}
      </div>

      {/* Sub-scores */}
      {qcSummary.subScores && Object.keys(qcSummary.subScores).length > 0 && (
        <div className="space-y-3">
          <div className="text-sm font-medium text-slate-300 flex items-center gap-2">
            <Target className="w-4 h-4" />
            Category Scores
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(qcSummary.subScores).map(([category, score]) => (
              <ScoreBar
                key={category}
                label={category.charAt(0).toUpperCase() + category.slice(1)}
                value={score}
              />
            ))}
          </div>
        </div>
      )}

      {/* Report Link */}
      {qcSummary.reportRef && (
        <div className="pt-2 border-t border-slate-700/50">
          <a
            href={qcSummary.reportRef}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
          >
            <FileText className="w-4 h-4" />
            View Full QC Report
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
    </motion.div>
  );
};

export default QCPanel;